import "@tento-ui/component-builtin";
import "@tento-ui/components/presentation/presentation-asset";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";

/**
 * Onboarding "Start here" card — the first card a new user sees.
 *
 * Card mode is a self-contained hero (the host card hides its header via
 * `hideCardHeader`): a stars illustration on top, then the tour headline and a
 * circular Start button. The button typography/shape are forked from
 * `std-brand-card` / `StickerDetachButton`.
 */
@component({ name: "std-nux-explainer-card" })
export class NuxExplainerCard extends LonaWebComponent {
  private onStart: Option<() => void> = null;

  static makeWith(callbacks: NuxExplainerCard.Callbacks): NuxExplainerCard {
    return NuxExplainerCard.make().bind(callbacks);
  }

  bind(callbacks: NuxExplainerCard.Callbacks): this {
    this.onStart = callbacks.onStart;
    GESTURE_MANAGER.addPointerEvent(this.$("start"), {
      onClick: () => this.onStart?.(),
    });
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        height: 100%;
      }

      #container {
        height: 100%;
        gap: 0;
        align-items: stretch;
      }

      /* Stars hero illustration in a neutral muted panel (the landing card's
         .media-placeholder aesthetic). Prefers ~60% of the card height and
         never grows, but flex-shrinks when the card is short so the body
         content (title + start button) is never pushed out. min-height:0
         overrides the default auto min so it can shrink past the hero glyph. */
      #illustration {
        flex: 0 1 60%;
        min-height: 0;
        margin: 8px;
        border-radius: 20px;
        background: color-mix(in srgb, var(--bg) 86%, var(--text-pri) 14%);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      }

      #hero {
        width: 48px;
        height: 48px;
      }

      /* The body flex-grows to fill leftover space; the illustration only
         shrinks (never grows), so extra height goes here — not both. */
      #body {
        flex-grow: 1;
        align-items: start;
        gap: 16px;
        padding: 24px 20px 24px;
      }

      /* Forked from std-brand-card .card-title (brand/landing/landing-card.ts). */
      .title {
        color: var(--text-pri);
        font-weight: 300;
        --font-size: var(--text-14px);
        --line-height-ratio: 1.15;
        --margin-top: 0px;
        --margin-bottom: 0px;
      }

      #start {
        align-items: center;
        gap: 12px;
        cursor: pointer;
      }

      /* Forked from StickerDetachButton (brand/landing/sticker-detach-button.ts). */
      #start-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0px;
        border: none;
        border-radius: 50%;
        background: var(--text-pri);
        color: var(--bg);
        cursor: pointer;
        flex-shrink: 0;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
        transition: opacity 0.15s ease;
      }

      #start:active #start-btn {
        opacity: 0.9;
      }

      #start-btn svg {
        width: 16px;
        height: 16px;
        display: block;
      }
    `,
  ];

  static $html = html`
    <std-col id="container">
      <div id="illustration">
        <std-presentation-asset
          id="hero"
          src="illustration/stars"
          decorative
        ></std-presentation-asset>
      </div>
      <std-col id="body">
        <p class="title">Let's take a quick tour.</p>
        <std-row id="start">
          <button id="start-btn" type="button" aria-label="Start now">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <line x1="5" y1="12" x2="18" y2="12"></line>
              <polyline points="12 6 18 12 12 18"></polyline>
            </svg>
          </button>
        </std-row>
      </std-col>
    </std-col>
  `;
}

export namespace NuxExplainerCard {
  export type Callbacks = {
    onStart: () => void;
  };
}
